import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useTableState from "@/hooks/useTableState";
import { ColumnVisibility, TableState } from "@/types/tableState.types";
import TableToolbar from "@/components/UI/TableToolbar/TableToolbar";
import AppliedFiltersSection from "@/components/UI/TableToolbar/Filtering/AppliedFiltersSection";
import { useTableDictionary } from "@/utils/tableDictionaries/useTableDictionary";
import { useScopeParams } from "@/hooks/useScopeParams";
import VivoPlotModal from "@/components/VivoPlots/modal";
import { VivoPlotProps } from "@/components/VivoPlots/types";
import { createNewPlotConfig } from "@/components/VivoPlots/usePlotSetup";
import { Button, Modal } from "antd";
import { useTableHeight } from "@/utils/tableHelpers";
import ViewSwitcher from "../UI/TableToolbar/ViewSwitcher";
import type { DashboardTemplateProps, DeleteModalProps } from "./types";
import { DashboardLayoutGuardModal } from "./DashboardLayoutGuardModal";
import { DashboardGrid } from "./DashboardGrid";
import {
  DatapointContextMenu,
  type DatapointMenuTarget,
} from "./DatapointContextMenu";
import { useDashboardLayoutEditing } from "@/hooks/useDashboardLayoutEditing";
import { usePlotFunctions } from "@/hooks/usePlotFunctions";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useScreenViewUrlSync } from "@/hooks/useScreenViewUrlSync";
import { DashboardTable } from "./DashboardTable";
import { applySelectionToFilters } from "@/components/VivoPlots/functions/utils/plotSelection";
import type { PlotElementSelection } from "@/components/VivoPlots/functions/utils/plotSelection";
import {
  buildDashboardPlotFilters,
  buildSiteScopeFilter,
  getDashboardMetadataForPage,
  getDashboardPageName,
  getDashboardPlotMetadata,
  omitDashboardFilterKeys,
} from "./dashboardTemplateUtils";

const DeleteModal = ({ isOpen, onCancel, onConfirm }: DeleteModalProps) => {
  return (
    <Modal
      open={isOpen}
      footer={
        <div className="flex items-center justify-between">
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            onClick={() => {
              onConfirm();
              onCancel();
            }}
            className="bg-editor-danger text-white  hover:bg-white! hover:text-editor-danger! border-editor-danger border-2 hover:border-editor-danger!"
          >
            Delete
          </Button>
        </div>
      }
      closable={true}
      centered
      width={422}
      title="Delete Plot?"
    >
      <p className="text-brand-dark text-base">
        Are you sure you want to delete this plot from this view?{" "}
      </p>
    </Modal>
  );
};

export const DashboardTemplate = ({
  metadata,
  datasetName,
  constants,
  subjectId,
  siteId,
  workspace,
  extraTabs = [],
  extraColumns = [],
  screenViewUrlParam,
  selectionIdentifierColumn = "subject",
  endpointPrefix,
  presetFilters,
  rowKey = (record) =>
    record.participant_id ??
    record.id ??
    record.subject ??
    record.user_email ??
    JSON.stringify(record),
  onRow,
  dataPageNameField,
  pinnedFilters,
  rowSelection,
  onTableContextChange,
}: DashboardTemplateProps) => {
  const fullPageName = subjectId ? `${datasetName}-ssj` : datasetName;
  const hasFixedSubjectFilter = Boolean(subjectId);
  const { id: trialId } = useScopeParams();
  const { columnsTitleCreator } = useTableDictionary(datasetName);

  const {
    currentWorkspace,
    setCurrentWorkspace,
    workspaces,
    initialTableState,
    mutations: workspaceMutations,
  } = workspace;

  const activeDataPage = dataPageNameField
    ? pinnedFilters?.[dataPageNameField]?.[0]
    : undefined;
  const pinnedFilterKeys = Object.keys(pinnedFilters || {});

  const activeMetadata = useMemo(
    () => getDashboardMetadataForPage(metadata, activeDataPage),
    [activeDataPage, metadata],
  );

  // The API hands back columns as {field, label}; useTableState wants field-name
  // strings. Passing the objects straight through produced table state whose
  // fieldName WAS an object, so every cell bound to nothing and rendered "-".
  const activeMetadataColumnNames = useMemo(
    () =>
      (activeMetadata.columns ?? [])
        .map((column: unknown) =>
          typeof column === "string"
            ? column
            : (column as { field?: string })?.field,
        )
        .filter((field): field is string => Boolean(field)),
    [activeMetadata.columns],
  );

  const { currentTableState, tableStateFunctions } = useTableState({
    initialState: {
      ...initialTableState,
      filters: {
        ...initialTableState.filters,
        ...(subjectId ? { subject: [subjectId] } : {}),
      },
      pagination: {
        ...initialTableState.pagination,
        total: metadata.row_count,
      },
    },
    pageName: getDashboardPageName({
      datasetName,
      subjectId,
      activeDataPage,
      isTabbed: Boolean(dataPageNameField),
    }),
    isSingleSubject: Boolean(subjectId),
    trialId,
    metadataColumns: activeMetadataColumnNames,
    metadataFilterKeys: Object.keys(activeMetadata.filters || {}),
  });

  const hiddenColumnsList = currentTableState.columns
    .filter((column) => column.visibility === ColumnVisibility.HIDDEN)
    .map((column) => column.fieldName);

  const hasAdditionalFilters = Object.entries(
    currentTableState.filters || {},
  ).some(
    ([key, values]) =>
      (key !== "subject" || !hasFixedSubjectFilter) &&
      key !== dataPageNameField &&
      !pinnedFilterKeys.includes(key) &&
      values.length > 0,
  );

  const excludedFilterKeys = [
    ...(dataPageNameField ? [dataPageNameField] : []),
    ...pinnedFilterKeys,
  ];
  const filterMetadata = omitDashboardFilterKeys(
    activeMetadata.filters,
    excludedFilterKeys,
  );
  const filterOptions = omitDashboardFilterKeys(
    filterMetadata,
    hiddenColumnsList,
  );

  // Only columns the dataset can actually filter on, minus the ones this screen
  // holds fixed, may be driven from a plot: anything else would build a request
  // the backend rejects or silently fight a pinned filter.
  const filterableColumns = useMemo(
    () => Object.keys(filterMetadata || {}),
    [filterMetadata],
  );

  const onSelectPlotElement = useCallback(
    (selection: PlotElementSelection) => {
      const activeFilters = currentTableState.filters || {};
      const nextFilters = applySelectionToFilters(
        activeFilters,
        selection,
        new Set(filterableColumns),
      );
      // Same reference back means nothing in the selection was filterable.
      if (nextFilters === activeFilters) return;
      tableStateFunctions.setFilters(nextFilters);
    },
    [currentTableState.filters, filterableColumns, tableStateFunctions],
  );

  const pageTotalCountsRef = useRef<Record<string, number>>({});
  const pageMetadataTotal = activeDataPage
    ? metadata.pages?.[activeDataPage]?.row_count
    : undefined;
  const needsUnfilteredPageCount = Boolean(
    activeDataPage &&
    pageMetadataTotal === undefined &&
    pageTotalCountsRef.current[activeDataPage] === undefined &&
    hasAdditionalFilters,
  );

  const tableRef = useRef<HTMLDivElement>(null);

  const resolvedSelectionIdentifierColumn =
    selectionIdentifierColumn?.trim() || "subject";
  // metadata.columns are {field, label} objects, so a bare includes() of a field
  // name never matched and row selection was silently dead on every screen.
  const hasValidSelectionIdentifierColumn = metadata.columns.some(
    (column: unknown) =>
      (typeof column === "string"
        ? column
        : (column as { field?: string })?.field) ===
      resolvedSelectionIdentifierColumn,
  );

  const activeSelectionKey = dataPageNameField
    ? (activeDataPage ?? "__all__")
    : "__all__";
  const [selectedIdentifierByPage, setSelectedIdentifierByPage] = useState<
    Record<string, string | undefined>
  >({});
  const selectedIdentifierValue = selectedIdentifierByPage[activeSelectionKey];
  const toggleSelectedIdentifier = (identifierValue: string | null) => {
    if (!hasValidSelectionIdentifierColumn || !identifierValue) return;
    setSelectedIdentifierByPage((currentByPage) => {
      const currentIdentifierValue = currentByPage[activeSelectionKey];
      return {
        ...currentByPage,
        [activeSelectionKey]:
          identifierValue === currentIdentifierValue
            ? undefined
            : identifierValue,
      };
    });
  };

  const [datapointMenuTarget, setDatapointMenuTarget] =
    useState<DatapointMenuTarget | null>(null);
  const dismissDatapointMenu = useCallback(
    () => setDatapointMenuTarget(null),
    [],
  );
  const onPointActivate = (entityKey: string, event: MouseEvent) =>
    setDatapointMenuTarget({ entityKey, x: event.clientX, y: event.clientY });

  const [plotModalData, setPlotModalData] = useState<VivoPlotProps | null>(
    null,
  );
  const [deleteModalPlotId, setDeleteModalPlotId] = useState<string | null>(
    null,
  );
  const [isDashboardEditorOpen, setIsDashboardEditorOpen] = useState(false);
  const [pendingScreenView, setPendingScreenView] = useState<string | null>(
    null,
  );

  // Spread last everywhere: a site-scoped screen must not be widened by a
  // pinned, preset or user-chosen filter.
  const siteScopeFilter = buildSiteScopeFilter(siteId);

  const workspaceFiltersForPlots = buildDashboardPlotFilters(
    currentTableState.filters,
    subjectId,
    { ...(pinnedFilters ?? {}), ...siteScopeFilter },
  );

  const downloadFixedFilters = {
    ...(pinnedFilters ?? {}),
    ...(presetFilters ?? {}),
    ...siteScopeFilter,
  };

  const plotMetadata = getDashboardPlotMetadata(
    activeMetadata,
    metadata,
    dataPageNameField,
  );

  const {
    data,
    dataEndpointSuffix,
    loadingData,
    refreshingData,
    unfilteredCount,
  } = useDashboardData({
    currentTableState,
    datasetName,
    endpointPrefix,
    metadata: activeMetadata,
    subjectId,
    tableStateFunctions,
    fixedFilters: downloadFixedFilters,
    excludeFilterKeys: dataPageNameField ? [dataPageNameField] : undefined,
    unfilteredCountEnabled: needsUnfilteredPageCount,
  });

  useEffect(() => {
    if (activeDataPage && data?.count !== undefined && !hasAdditionalFilters) {
      pageTotalCountsRef.current[activeDataPage] = data.count;
    }
  }, [activeDataPage, data?.count, hasAdditionalFilters]);

  const reportedTableContextRef = useRef("");
  useEffect(() => {
    if (!onTableContextChange || data?.count === undefined) return;
    const signature = JSON.stringify([workspaceFiltersForPlots, data.count]);
    if (reportedTableContextRef.current === signature) return;
    reportedTableContextRef.current = signature;
    onTableContextChange({
      filters: workspaceFiltersForPlots,
      filterMetadata: activeMetadata.filters,
      filteredCount: data.count,
    });
  }, [
    activeMetadata.filters,
    data?.count,
    onTableContextChange,
    workspaceFiltersForPlots,
  ]);
  const totalRecords = activeDataPage
    ? (pageMetadataTotal ??
      pageTotalCountsRef.current[activeDataPage] ??
      unfilteredCount ??
      data?.count ??
      0)
    : metadata.row_count;
  const hasSelectedRecords =
    data?.count !== undefined &&
    (pageMetadataTotal !== undefined ||
      pageTotalCountsRef.current[activeDataPage || ""] !== undefined ||
      unfilteredCount !== undefined) &&
    data.count !== totalRecords;

  const wrappedTableStateFunctions = {
    ...tableStateFunctions,
    reset: (overrideState?: TableState, callback?: () => void) => {
      tableStateFunctions.reset(overrideState, () => {
        setSelectedIdentifierByPage((currentByPage) => ({
          ...currentByPage,
          [activeSelectionKey]: undefined,
        }));
        setDatapointMenuTarget(null);
        callback?.();
      });
    },
  };

  const plotFunctions = usePlotFunctions(
    currentTableState,
    tableStateFunctions,
  );

  const { tableHeight } = useTableHeight({ tableRef: tableRef });

  const visiblePlots = useMemo(
    () =>
      ((currentTableState.plots || []) as VivoPlotProps[]).filter(
        (plot) => !plot.config.hidden,
      ),
    [currentTableState.plots],
  );
  const hasVisiblePlots = visiblePlots.length > 0;

  const {
    isLayoutEditMode,
    activeLayouts,
    enterLayoutEditMode,
    cancelLayoutEdit,
    saveLayout,
    onLayoutChange,
    compactLayout,
  } = useDashboardLayoutEditing({
    visiblePlots,
    savedLayouts: currentTableState.plotLayoutsByBreakpoint,
    persistLayouts: tableStateFunctions.setPlotLayoutsByBreakpoint,
  });

  const validScreenViews = [
    "table",
    "plots",
    ...extraTabs.map((tab) => tab.key),
  ];
  const activeScreenView =
    currentTableState.screenView &&
    validScreenViews.includes(currentTableState.screenView)
      ? currentTableState.screenView
      : "table";

  const writeScreenViewToUrl = useScreenViewUrlSync({
    param: screenViewUrlParam,
    activeScreenView,
    validScreenViews,
    setScreenView: tableStateFunctions.setScreenView,
  });

  const applyScreenView = (screen: string) => {
    // The menu is rendered only on the plots view, so a target that outlived a
    // view change would reappear at a stale cursor position on the way back.
    setDatapointMenuTarget(null);
    tableStateFunctions.setScreenView(screen);
    writeScreenViewToUrl(screen);
  };

  const requestScreenViewChange = (screen: string) => {
    if (screen === currentTableState.screenView) return;
    if (!isLayoutEditMode) {
      applyScreenView(screen);
      return;
    }
    setPendingScreenView(screen);
  };

  const handleGuardCancel = () => {
    setPendingScreenView(null);
  };

  const continueAfterLayoutEdit = (shouldSave: boolean) => {
    const nextScreen = pendingScreenView;
    if (shouldSave) saveLayout();
    else cancelLayoutEdit();
    setPendingScreenView(null);

    if (nextScreen) {
      applyScreenView(nextScreen);
    }
  };

  useEffect(() => {
    if (!hasVisiblePlots) {
      /* eslint-disable @eslint-react/set-state-in-effect */
      setSelectedIdentifierByPage((currentByPage) => ({
        ...currentByPage,
        [activeSelectionKey]: undefined,
      }));
      setDatapointMenuTarget(null);
      setIsDashboardEditorOpen(false);
      /* eslint-enable @eslint-react/set-state-in-effect */
      cancelLayoutEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps, @eslint-react/exhaustive-deps
  }, [hasVisiblePlots]);

  return (
    <div className="mt-2">
      <VivoPlotModal
        columnsTitleCreator={columnsTitleCreator}
        globalFilters={workspaceFiltersForPlots}
        endpointSuffix={dataEndpointSuffix}
        endpointPrefix={endpointPrefix}
        metadata={plotMetadata}
        initialPlotSetup={plotModalData}
        datasetName={datasetName}
        isOpen={!!plotModalData}
        columns={currentTableState.columns}
        columnOptions={currentTableState.columns
          .filter(
            (column) => column.visibility !== ColumnVisibility.NOT_AVAILABLE,
          )
          .map((column) => column.fieldName)}
        onCancel={() => setPlotModalData(null)}
        onConfirm={(plot) => {
          const plots = currentTableState.plots || [];
          if (plots.every((p) => p?.config?.plotId !== plot.config.plotId)) {
            tableStateFunctions.setPlots([...plots, plot]);
          } else {
            plotFunctions.updatePlotById(plot.config.plotId, plot);
          }
        }}
        subjectId={subjectId}
      />
      <DeleteModal
        isOpen={!!deleteModalPlotId}
        onCancel={() => setDeleteModalPlotId(null)}
        onConfirm={() =>
          deleteModalPlotId && plotFunctions.onDeletePlot(deleteModalPlotId)
        }
      />
      <DashboardLayoutGuardModal
        open={isLayoutEditMode && pendingScreenView !== null}
        onCancel={handleGuardCancel}
        onDiscard={() => continueAfterLayoutEdit(false)}
        onSave={() => continueAfterLayoutEdit(true)}
      />

      <TableToolbar
        viewSwitcher={
          <ViewSwitcher
            screen={activeScreenView}
            setScreen={requestScreenViewChange}
            options={[
              { label: "Table", value: "table" },
              { label: "Dashboard", value: "plots" },
              ...extraTabs.map((tab) => ({
                label: tab.title,
                value: tab.key,
                icon: tab.icon,
              })),
            ]}
          />
        }
        workspace={{
          tableName: fullPageName,
          workspaces,
          currentWorkspace,
          setCurrentWorkspace,
          mutations: workspaceMutations,
          initialTableState,
        }}
        tableMetadata={{ ...activeMetadata, filters: filterMetadata }}
        maxMenuHeight={tableHeight}
        currentTableState={currentTableState}
        downloadFilters={workspaceFiltersForPlots}
        downloadFixedFilters={downloadFixedFilters}
        endpointSuffix={dataEndpointSuffix}
        endpointPrefix={endpointPrefix}
        guideLink={constants.wikiLink}
        datasetName={datasetName}
        name={datasetName}
        downloadName={[trialId, datasetName, activeDataPage]
          .filter(Boolean)
          .join("_")}
        fullPageName={fullPageName}
        tableStateFunctions={wrappedTableStateFunctions}
        isDashboardEditorOpen={isDashboardEditorOpen}
        onToggleDashboardEditor={() =>
          setIsDashboardEditorOpen((open) => !open)
        }
        onCloseDashboardEditor={() => setIsDashboardEditorOpen(false)}
        dashboardEditorProps={{
          setPlots: tableStateFunctions.setPlots,
          onAddNewWidget: () => {
            setPlotModalData(createNewPlotConfig());
            setIsDashboardEditorOpen(false);
          },
          onEditPlot: (plot) => {
            setPlotModalData(plot);
            setIsDashboardEditorOpen(false);
          },
          onEditLayout: enterLayoutEditMode,
        }}
        activeTabToolbarConfig={
          extraTabs.find((tab) => tab.key === activeScreenView)?.toolbar
        }
      />

      <div className="flex items-center justify-between gap-2 mb-4">
        <AppliedFiltersSection
          filters={workspaceFiltersForPlots}
          setFilters={tableStateFunctions.setFilters}
          filterOptions={filterOptions as Record<string, string[]>}
          allFiltersData={filterMetadata}
          clearFilters={() => tableStateFunctions.setFilters({})}
          columnsTitleCreator={columnsTitleCreator}
          hiddenFilterKeys={dataPageNameField ? [dataPageNameField] : undefined}
        />
      </div>
      {activeScreenView === "table" && (
        <DashboardTable
          constants={constants}
          currentTableState={currentTableState}
          data={data}
          loadingData={loadingData}
          refreshingData={refreshingData}
          metadata={metadata}
          totalRecords={totalRecords}
          hasSelectedRecords={hasSelectedRecords}
          setFilters={tableStateFunctions.setFilters}
          setColumn={tableStateFunctions.setColumn}
          setSorting={tableStateFunctions.setSorting}
          columnsTitleCreator={columnsTitleCreator}
          setPagination={tableStateFunctions.setPagination}
          tableHeight={tableHeight}
          tableRef={tableRef}
          rowKey={rowKey}
          selectionIdentifier={
            hasValidSelectionIdentifierColumn
              ? resolvedSelectionIdentifierColumn
              : undefined
          }
          rowIsSubject={datasetName === "subjects_overview"}
          extraColumns={extraColumns}
          onRow={onRow}
          rowSelection={rowSelection}
        />
      )}
      {activeScreenView === "plots" && (
        <DatapointContextMenu
          target={datapointMenuTarget}
          idLink={constants.idLink}
          identifierLabel={resolvedSelectionIdentifierColumn}
          onDismiss={dismissDatapointMenu}
        />
      )}
      {activeScreenView === "plots" && (
        <DashboardGrid
          visiblePlots={visiblePlots}
          activeLayouts={activeLayouts}
          isLayoutEditMode={isLayoutEditMode}
          onLayoutChange={onLayoutChange}
          onCancelEdit={cancelLayoutEdit}
          onSaveEdit={saveLayout}
          onCompactLayout={compactLayout}
          onCreatePlot={() => setPlotModalData(createNewPlotConfig())}
          globalFilters={workspaceFiltersForPlots}
          columns={currentTableState.columns}
          endpointSuffix={dataEndpointSuffix}
          endpointPrefix={endpointPrefix}
          editPlot={setPlotModalData}
          hidePlot={plotFunctions.onHidePlot}
          deletePlot={setDeleteModalPlotId}
          duplicatePlot={plotFunctions.onDuplicatePlot}
          onConfigChange={plotFunctions.onConfigChange}
          selectedIdName={
            hasValidSelectionIdentifierColumn
              ? resolvedSelectionIdentifierColumn
              : undefined
          }
          selectedIdValue={
            hasValidSelectionIdentifierColumn
              ? selectedIdentifierValue
              : undefined
          }
          setSelectedId={
            hasValidSelectionIdentifierColumn
              ? toggleSelectedIdentifier
              : undefined
          }
          onSelectPlotElement={onSelectPlotElement}
          filterableColumns={filterableColumns}
          appliedFilters={currentTableState.filters}
          onPointActivate={
            hasValidSelectionIdentifierColumn && constants.idLink
              ? onPointActivate
              : undefined
          }
          metadata={metadata}
          columnsTitleCreator={columnsTitleCreator}
          datasetName={datasetName}
          subjectId={subjectId}
        />
      )}
      {extraTabs.map((tab) =>
        activeScreenView === tab.key ? (
          <div key={tab.key}>
            <tab.component
              data={refreshingData ? [] : data?.results || []}
              filters={currentTableState.filters}
              // A custom tab is a peer of the table, not a read-only preview:
              // it needs the dataset metadata to know what the backend supports
              // and setFilters to participate in the screen's filter state.
              // Without these the Site map's radius tool never renders.
              setFilters={tableStateFunctions.setFilters}
              metadata={activeMetadata}
              datasetName={datasetName}
              endpointPrefix={endpointPrefix}
            />
          </div>
        ) : null,
      )}
    </div>
  );
};
